/* eslint-disable no-shadow */
import React, { useMemo, useState } from 'react';

import { Table, Tbody, Td, Box } from '@chakra-ui/react';
import { Collapse, TableCell, TableContainer, TableRow } from '@material-ui/core';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useOpenCompetencyDrawer } from 'components/Develop/helpers/useOpenCompetencyDrawer';
import { useAuthenticatedUser } from 'components/hooks/dataHooks';
import { useSelector } from 'react-redux';
import { SubCompetencyRow } from 'screens/Levelup/components/common/SubCompetencyList';
import { LEVELUP_COMPETENCIES_TABLE_HEADERS_MAP } from 'screens/Levelup/constants';
import { useLevelUpPageContext } from 'screens/Levelup/context';
import { Loader } from 'screens/analytics/commonComponents';
import { RightArrowIcon } from 'screens/common/sidepanel/Agenda/commonComponents';
import useLevelUpSidePanelContext from 'screens/oneOnOne/levelUp/competencySidePanel/context';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import { StyledBorderedPaper } from 'styles/common';
import { checkLevelUpModuleEnabled } from 'utils/helpers/moduleAccess';

import { Paper } from 'uiToolkitV1/Paper';
import { TableBodyRow, TableFoot, TableHeaderRow } from 'uiToolkitV1/TableComponents';
import { TablePagination } from 'uiToolkitV1/TableComponents/Pagination';
import { TableHeaderCell } from 'uiToolkitV1/TableComponents/TableHeader';
import { TableHead } from 'uiToolkitV1/TableComponents/TableHeader/TableHead';
import { TableHoverBodyRow } from 'uiToolkitV1/TableComponents/TableHoverBodyRow';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { usePerformancePreviewCompetencyData } from './hooks/dataHooks';

const StyledTableContainer = styled(TableContainer)`
  min-width: 1000px;
  .MuiTableCell-stickyHeader {
    background-color: white;
    z-index: 9;
  }
  padding: 16px;
`;

const StyledRightArrowIcon = styled(RightArrowIcon)`
  visibility: hidden;
  cursor: pointer;
`;

const StyledTableCell = styled(TableCell)`
  position: relative;
  border-left: ${(props) => props.borderLeft && `1px solid ${props.theme.palette.borders.lighterGrey}`};
  cursor: ${({ isSortable, clickable }) => (isSortable || clickable ? 'pointer' : 'inherit')};
  ${(props) =>
    props.$isCompetencyNameCell &&
    css`
      cursor: pointer;
      &:hover {
        ${StyledRightArrowIcon} {
          visibility: visible;
        }
        background-color: ${(props) => props.theme.palette.background.blue.light.v2};
      }
    `}
`;

const StyledTableRow = styled(TableRow)`
  th,
  td {
    border-bottom: 1px solid ${(props) => props.theme.palette.borders.lighterGrey};
  }
`;

const getFlexJustifyFromCellAlign = (align) => {
  switch (align) {
    case 'left':
      return 'flex-start';
    case 'center':
      return 'center';
    case 'right':
      return 'flex-end';
    default:
      return 'center';
  }
};

const performancePreviewCompetencyHeader = [
  LEVELUP_COMPETENCIES_TABLE_HEADERS_MAP.NAME.id,
  LEVELUP_COMPETENCIES_TABLE_HEADERS_MAP.WEIGHTAGE.id,
  LEVELUP_COMPETENCIES_TABLE_HEADERS_MAP.PROGRESS.id,
];

const renderRowSubComponent = ({ row }) => {
  const { subCompetencies } = row.original ?? {};
  if (!subCompetencies) {
    return [];
  }

  if (Array.isArray(subCompetencies)) {
    return subCompetencies.map(({ name, subCompetencyId }, index) => (
      <StyledTableRow style={{ borderBottom: 'none' }}>
        <SubCompetencyRow
          name={name}
          key={subCompetencyId}
          isLast={index === subCompetencies.length - 1}
          isSubManagerRow={false}
        />
      </StyledTableRow>
    ));
  }
  return [];
};

const ReviewPerformancePreviewCompetencyTable = ({ isPerformanceReviewTable = false }) => {
  const {
    state: { isLoading },
  } = useLevelUpPageContext();

  const { openLevelUpSidePanel } = useLevelUpSidePanelContext();

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const loggedInEmployeeData = useSelector(selectLoggedInEmployeeData);
  const { id: employeeId } = loggedInEmployeeData;

  const { data } = usePerformancePreviewCompetencyData({ employeeId });
  const { openCompetencyPanel } = useOpenCompetencyDrawer();
  const {
    user: { id },
  } = useAuthenticatedUser();

  const {
    state: { reporteeId, checkInInstanceId, checkInParentId, filterEmpIds, isLoggedInEmployeeManager },
  } = useLevelUpPageContext();

  const handleRowClick = ({
    reporteeId,
    competencyId,
    levelUpId,
    isLoggedInEmployeeManager,
    checkInInstanceId,
    checkInParentId,
    filterEmpIds,
    name,
  }) => {
    if (!checkLevelUpModuleEnabled()) {
      openCompetencyPanel({
        tagId: competencyId,
        tagTitle: name,
        employeeId: employeeId ?? id,
      });
    }
    openLevelUpSidePanel({
      reporteeId,
      competencyId,
      levelUpId,
      levelUpCompetencyId: null,
      isLoggedInEmployeeManager,
      checkInInstanceId,
      checkInParentId,
      filterEmpIds,
    });
  };

  const mappedHeaders = useMemo(
    () =>
      performancePreviewCompetencyHeader.map((enabledStage) => {
        const justify = getFlexJustifyFromCellAlign(LEVELUP_COMPETENCIES_TABLE_HEADERS_MAP[enabledStage]?.align);
        return {
          justify,
          ...LEVELUP_COMPETENCIES_TABLE_HEADERS_MAP[enabledStage],
          title:
            typeof LEVELUP_COMPETENCIES_TABLE_HEADERS_MAP[enabledStage].title === 'function'
              ? LEVELUP_COMPETENCIES_TABLE_HEADERS_MAP[enabledStage].title()
              : LEVELUP_COMPETENCIES_TABLE_HEADERS_MAP[enabledStage].title,
        };
      }),
    [performancePreviewCompetencyHeader]
  );

  const dataRows = useMemo(() => {
    if (!data) {
      return [];
    }

    return data?.map((rowObj, index) => ({ ...rowObj, index }));
  }, [data, mappedHeaders]);

  //   init react table
  const table = useReactTable({
    data: dataRows,
    columns: mappedHeaders,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => Boolean(row?.original?.subCompetencies?.length),
    debugTable: true,
  });

  if (!data || !data?.length) {
    return null;
  }

  if (isLoading) {
    return (
      <StyledBorderedPaper overFlow="auto" marginBottom="2rem" padding="0" topGutter={2}>
        <Loader />
      </StyledBorderedPaper>
    );
  }

  return (
    <Box px={4}>
      <Paper px={3} pb={3} pt={6}>
        <TextBodyMedium bold p="8px 24px" mb={2}>
          Competencies ({table.getRowModel().rows.length})
        </TextBodyMedium>
        <StyledTableContainer>
          <Table>
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableHeaderRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const { width, fixedWidth, align, borderLeft } = header?.column?.columnDef ?? {};
                    const justify = getFlexJustifyFromCellAlign(align);
                    return (
                      <TableHeaderCell
                        size="small"
                        variant="head"
                        width={isPerformanceReviewTable ? `min(${width}, 50%)` : width}
                        fixedWidth={fixedWidth}
                        align={align}
                        key={header.id}
                        colSpan={header.colSpan}
                        justify={justify}
                        borderLeft={borderLeft}
                        sx={{ padding: '8px 16px !important', div: { justifyContent: justify } }}
                      >
                        {header.isPlaceholder ? null : (
                          <>{flexRender(header.column.columnDef.header, header.getContext())}</>
                        )}
                      </TableHeaderCell>
                    );
                  })}
                </TableHeaderRow>
              ))}
            </TableHead>
            <Tbody>
              {table.getRowModel().rows.map((row) => {
                const isRowExpanded = row?.getIsExpanded();

                return (
                  <React.Fragment key={row.id}>
                    <TableHoverBodyRow key={row.id} bg={isRowExpanded ? 'neutral.F2F2F2' : 'initial'}>
                      {row.getVisibleCells().map((cell, index) => {
                        const competencyId = cell?.row?.original?.competencyId;
                        const levelUpId = cell?.row?.original?.levelUpId;
                        const name = cell?.row?.original?.name;
                        return (
                          <Td
                            borderLeft={index}
                            key={cell.id}
                            sx={{
                              position: 'relative',
                              padding: '16px',
                              cursor: 'pointer',
                            }}
                            onClick={() =>
                              handleRowClick({
                                reporteeId,
                                competencyId,
                                levelUpId,
                                isLoggedInEmployeeManager,
                                checkInInstanceId,
                                checkInParentId,
                                filterEmpIds,
                                name,
                              })
                            }
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </Td>
                        );
                      })}
                    </TableHoverBodyRow>
                    {isRowExpanded && (
                      <TableBodyRow key={row.id} bg="neutral.F2F2F2">
                        <StyledTableCell
                          style={{
                            padding: 0,
                            border: 'unset',
                          }}
                          colSpan="10" // <- used to control width of sub-table
                          sx={{ position: 'relative', padding: '16px' }}
                        >
                          <Collapse in={isRowExpanded} timeout="0" unmountOnExit>
                            {renderRowSubComponent({ row })}
                          </Collapse>
                        </StyledTableCell>
                      </TableBodyRow>
                    )}
                  </React.Fragment>
                );
              })}
            </Tbody>
            <TableFoot>
              <TablePagination
                count={dataRows?.length ?? 0}
                page={page}
                onPageChange={(_, pageNum) => {
                  setPage(pageNum);
                  table.setPageIndex(pageNum);
                }}
                handleChangeRowsPerPage={({ target }) => {
                  setPage(0);
                  table.setPageIndex(0);
                  setRowsPerPage(Number(target.value));
                  table.setPageSize(Number(target.value));
                }}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10, 20, 30]}
              />
            </TableFoot>
          </Table>
        </StyledTableContainer>
      </Paper>
    </Box>
  );
};

export default ReviewPerformancePreviewCompetencyTable;
